/**
 * Google Analytics 4 (GA4) Analytics Utility
 * 
 * Centralized analytics tracking functions for GA4.
 * Only tracks when GA4_MEASUREMENT_ID is set.
 */

import { GA4_MEASUREMENT_ID, ENV } from './env';

// Extend Window interface for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Check if analytics is enabled
 */
export const isAnalyticsEnabled = (): boolean => {
  return !!GA4_MEASUREMENT_ID && GA4_MEASUREMENT_ID.trim() !== '';
};

/**
 * Initialize Google Analytics 4
 */
export const initAnalytics = (): void => {
  if (!isAnalyticsEnabled()) {
    if (ENV.DEV) {
      console.log('📊 Analytics disabled: GA4_MEASUREMENT_ID not set');
    }
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function
  window.gtag = function(...args: any[]) {
    window.dataLayer.push(args);
  };

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Configure GA4
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    page_path: window.location.pathname,
  });

  if (ENV.DEV) {
    console.log('✅ Google Analytics 4 initialized');
  }
};

/**
 * Track a page view
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isAnalyticsEnabled()) return;

  const pageTitle = title || document.title;
  
  window.gtag('config', GA4_MEASUREMENT_ID, {
    page_path: path,
    page_title: pageTitle,
  });
};

/**
 * Track a custom event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', eventName, eventParams || {});
};

/**
 * Track a conversion event
 */
export const trackConversion = (
  eventName: string,
  value?: number,
  currency: string = 'USD'
): void => {
  if (!isAnalyticsEnabled()) return;

  const params: Record<string, any> = {};
  if (value !== undefined) {
    params.value = value;
    params.currency = currency;
  }

  window.gtag('event', eventName, params);
};

/**
 * Track user sign-up
 */
export const trackSignUp = (method?: string): void => {
  trackEvent('sign_up', {
    method: method || 'email',
  });
};

/**
 * Track user sign-in/login
 */
export const trackSignIn = (method?: string): void => {
  trackEvent('login', {
    method: method || 'email',
  });
};

/**
 * Track form submission
 */
export const trackFormSubmit = (formName: string, success: boolean = true): void => {
  trackEvent('form_submit', {
    form_name: formName,
    success: success,
  });
};

/**
 * Track button click
 */
export const trackButtonClick = (buttonName: string, location?: string): void => {
  trackEvent('button_click', {
    button_name: buttonName,
    location: location || window.location.pathname,
  });
};

/**
 * Track error/exception
 */
export const trackError = (
  errorDescription: string,
  fatal: boolean = false
): void => {
  trackEvent('exception', {
    description: errorDescription,
    fatal: fatal,
  });
};

/**
 * Track 404 error
 */
export const track404 = (path: string): void => {
  trackEvent('page_not_found', {
    page_path: path,
  });
};

/**
 * Track search (if you add search functionality)
 */
export const trackSearch = (searchTerm: string): void => {
  trackEvent('search', {
    search_term: searchTerm,
  });
};

/**
 * Track share (if you add social sharing)
 */
export const trackShare = (contentType: string, itemId?: string): void => {
  trackEvent('share', {
    content_type: contentType,
    item_id: itemId,
  });
};

